/**
 * services/production.service.ts - Služby pro analýzu vyrobených kusů (OK production)
 *
 * FUNKCE:
 * - Souhrnná statistika vyrobených OK kusů (celkový počet, průměr, nejproduktivnější den, volatilita)
 * - Denní trendy produkce OK kusů (celkem i po různých dimenzích)
 * - Produkce podle pracovišť, projektů, směnových týmů a typů směn
 * - Heatmapy produkce podle dnů v týdnu a hodin
 *
 * ZDROJE DAT:
 * - Shifts (okCount) - základní data o produkci OK kusů
 *
 * KLÍČOVÉ METRIKY:
 * - Total OK Pieces (celkový počet OK kusů) = sum(okCount)
 * - Average Daily Production (průměrná denní produkce) = sum(okCount) / počet dnů
 * - Volatility (volatilita produkce) = směrodatná odchylka denní produkce
 * - Most Productive Day (nejproduktivnější den) = den s nejvyšším okCount
 *
 * VÝROBNÍ DNY:
 * Všechny denní agregace používají productionDayKey(), takže respektují výrobní den
 * definovaný v TenantModel.dayStart.
 * 
 * OPTIMALIZACE:
 * - getAllProductionAnalytics() - hlavní funkce, která načte data jen 2x (current + previous)
 *   a pak provede všechny analýzy v paměti bez dalších GraphQL volání
 */

import moment from 'moment-timezone';

// Timezone pro zobrazování a výpočty - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';

import { ShiftsRepository } from '@/libs/tpm-analytics/data/shifts.repository';
import { createGrid, topNIndexes } from '@/libs/tpm-analytics/core/aggregation.utils';
import { productionDayKey } from '@/libs/tpm-analytics/core/production-day.utils';
import {
    ProductionSummary,
    ProductionTrendPoint,
    ProductionByWorkplace,
    ProductionTrendByWorkplace,
    ProductionByProject,
    ProductionByShiftTeam,
    ProductionByShiftType,
    ProductionHeatmapResult,
} from '@/libs/tpm-analytics/core/types';

/**
 * Komplexní výsledek všech produkčních analýz
 */
export interface AllProductionAnalytics {
    summary: ProductionSummary;
    trend: ProductionTrendPoint[];
    byWorkplace: ProductionByWorkplace[];
    workplaceTrend: ProductionTrendByWorkplace[];
    byProject: ProductionByProject[];
    byShiftTeam: ProductionByShiftTeam[];
    byShiftType: ProductionByShiftType[];
    heatmap: ProductionHeatmapResult;
}

/**
 * HLAVNÍ OPTIMALIZOVANÁ FUNKCE - načte směny pouze 2x a provede všechny analýzy
 * 
 * @param fromIso Začátek období
 * @param toIso Konec období
 * @param granularity Granularita pro trendy
 * @param filters Filtry
 * @returns Všechny produkční analýzy najednou
 */
export async function getAllProductionAnalytics(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<AllProductionAnalytics> {
    const from = moment.tz(fromIso, DISPLAY_TIMEZONE);
    const to = moment.tz(toIso, DISPLAY_TIMEZONE);
    const span = Math.max(1, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-span, 'minutes');

    // POUZE 2 GraphQL VOLÁNÍ
    const [shiftsNow, shiftsPrev] = await Promise.all([
        ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters),
        ShiftsRepository.fetchShiftsForRange([], prevFrom.toISOString(), prevTo.toISOString(), filters),
    ]);

    // Všechny následující funkce pracují s již načtenými daty
    const summary = computeProductionSummary(shiftsNow, shiftsPrev, from, to);
    const trend = computeProductionTrend(shiftsNow, granularity);
    const byWorkplace = computeProductionByWorkplace(shiftsNow, from, to);
    const workplaceTrend = computeProductionTrendByWorkplace(shiftsNow, granularity);
    const byProject = computeProductionByProject(shiftsNow, granularity);
    const byShiftTeam = computeProductionByShiftTeam(shiftsNow, granularity);
    const byShiftType = computeProductionByShiftType(shiftsNow, granularity);
    const heatmap = computeProductionHeatmap(shiftsNow, from, to);

    return {
        summary,
        trend,
        byWorkplace,
        workplaceTrend,
        byProject,
        byShiftTeam,
        byShiftType,
        heatmap,
    };
}

/**
 * INTERNÍ FUNKCE - Výpočet shrnutí produkce z již načtených dat
 */
function computeProductionSummary(
    shiftsNow: any[],
    shiftsPrev: any[],
    from: moment.Moment,
    to: moment.Moment
): ProductionSummary {
    // Agregace podle výrobních dnů
    const aggregateByDay = (shifts: any[]) => {
        const dayMap = new Map<string, number>();
        for (const s of shifts) {
            const ok = Number(s.okCount ?? 0) || 0;
            const fromDate = s.fromDate ? moment.tz(s.fromDate, DISPLAY_TIMEZONE).valueOf() : Date.now();
            const day = productionDayKey(fromDate);
            dayMap.set(day, (dayMap.get(day) || 0) + ok);
        }
        return dayMap;
    };

    const curDays = aggregateByDay(shiftsNow);
    const prevDays = aggregateByDay(shiftsPrev);

    // Celkový počet OK kusů
    const totalOkPieces = Array.from(curDays.values()).reduce((sum, count) => sum + count, 0);
    const prevTotalOkPieces = Array.from(prevDays.values()).reduce((sum, count) => sum + count, 0);

    // Průměrná denní produkce
    const totalDays = curDays.size || 1;
    const avgDailyProduction = totalOkPieces / totalDays;
    const prevNumDays = prevDays.size || 1;
    const prevAvgDailyProduction = prevTotalOkPieces / prevNumDays;

    // Maximální denní produkce
    let maxDailyProduction: number | null = null;
    for (const [day, count] of curDays.entries()) {
        if (maxDailyProduction === null || count > maxDailyProduction) {
            maxDailyProduction = count;
        }
    }

    // Nejproduktivnější dny v týdnu
    const weekdayMap = new Map<number, number>(); // 0=Po, 1=Út, ..., 6=Ne
    for (const [day, count] of curDays.entries()) {
        const date = moment(day);
        const weekday = (date.isoWeekday() - 1 + 7) % 7; // 0=Po, 6=Ne
        weekdayMap.set(weekday, (weekdayMap.get(weekday) || 0) + count);
    }
    const mostProductiveWeekdays = Array.from(weekdayMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([weekday]) => weekday);

    // Počet dní nad průměrem
    const values = Array.from(curDays.values());
    const daysAboveAverage = values.filter(v => v >= avgDailyProduction).length;

    // Konzistence produkce (% dní s alespoň 80% průměru)
    let consistencyPct: number | null = null;
    if (totalDays > 0) {
        const threshold = avgDailyProduction * 0.8;
        const consistentDays = values.filter(v => v >= threshold).length;
        consistencyPct = (consistentDays / totalDays) * 100;
    }

    // Průměrná produkce na směnu
    let avgProductionPerShift: number | null = null;
    if (shiftsNow.length > 0) {
        avgProductionPerShift = totalOkPieces / shiftsNow.length;
    }

    // Trend produkce (porovnání první a druhé poloviny období)
    let productionTrend: number | null = null;
    if (curDays.size >= 2) {
        const sortedDays = Array.from(curDays.entries()).sort((a, b) => a[0].localeCompare(b[0]));
        const midPoint = Math.floor(sortedDays.length / 2);
        const firstHalf = sortedDays.slice(0, midPoint);
        const secondHalf = sortedDays.slice(midPoint);
        
        const firstHalfSum = firstHalf.reduce((sum, [_, count]) => sum + count, 0);
        const secondHalfSum = secondHalf.reduce((sum, [_, count]) => sum + count, 0);
        const firstHalfAvg = firstHalfSum / firstHalf.length;
        const secondHalfAvg = secondHalfSum / secondHalf.length;
        
        if (firstHalfAvg > 0) {
            productionTrend = ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100;
        }
    }

    return {
        totalOkPieces,
        avgDailyProduction,
        mostProductiveWeekdays,
        maxDailyProduction,
        daysAboveAverage,
        totalDays,
        consistencyPct,
        avgProductionPerShift,
        productionTrend,
        prev: {
            totalOkPieces: prevTotalOkPieces,
            avgDailyProduction: prevAvgDailyProduction,
        },
    };
}

/**
 * INTERNÍ FUNKCE - Výpočet trendu produkce z již načtených dat
 */
function computeProductionTrend(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): ProductionTrendPoint[] {

    const dayMap = new Map<string, number>();
    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        let key: string;
        const m = moment.tz(s.fromDate, DISPLAY_TIMEZONE);
        if (granularity === 'week') {
            key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
        } else if (granularity === 'month') {
            key = m.clone().startOf('month').format('YYYY-MM-DD');
        } else {
            const fromDate = s.fromDate ? moment.tz(s.fromDate, DISPLAY_TIMEZONE).valueOf() : Date.now();
            key = productionDayKey(fromDate);
        }
        dayMap.set(key, (dayMap.get(key) || 0) + ok);
    }

    const sorted: ProductionTrendPoint[] = Array.from(dayMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([day, okCount]) => ({ day, okCount, movingAvg: null }));

    // Pohyblivý průměr (7 bodů)
    const windowSize = 7;
    for (let i = 0; i < sorted.length; i++) {
        const start = Math.max(0, i - Math.floor(windowSize / 2));
        const end = Math.min(sorted.length, i + Math.ceil(windowSize / 2));
        const window = sorted.slice(start, end);
        const sum = window.reduce((acc, p) => acc + p.okCount, 0);
        sorted[i].movingAvg = sum / window.length;
    }

    return sorted;
}

/**
 * INTERNÍ FUNKCE - Výpočet produkce podle pracovišť z již načtených dat
 */
function computeProductionByWorkplace(
    shifts: any[],
    from: moment.Moment,
    to: moment.Moment
): ProductionByWorkplace[] {
    const numDays = Math.max(1, to.diff(from, 'days'));

    const wpMap = new Map<string, { name: string; okCount: number }>();
    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        const wpId = s.workplaceId || 'unknown';
        const wpName = (s as any)?.workplace?.name || s.workplaceId || 'N/A';
        if (!wpMap.has(wpId)) {
            wpMap.set(wpId, { name: wpName, okCount: 0 });
        }
        const entry = wpMap.get(wpId)!;
        entry.okCount += ok;
    }

    return Array.from(wpMap.entries())
        .map(([workplaceId, data]) => ({
            workplaceId,
            workplaceName: data.name,
            okCount: data.okCount,
            avgPerDay: data.okCount / numDays,
        }))
        .sort((a, b) => b.okCount - a.okCount);
}

/**
 * INTERNÍ FUNKCE - Výpočet produkce podle pracovišť v čase z již načtených dat (pro trend graf)
 */
function computeProductionTrendByWorkplace(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): ProductionTrendByWorkplace[] {
    // workplaceId -> day -> okCount
    const wpMap = new Map<string, { name: string; dayMap: Map<string, number> }>();

    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        if (ok <= 0) continue;
        
        const wpId = s.workplaceId || 'unknown';
        const wpName = (s as any)?.workplace?.name || s.workplaceId || 'N/A';

        let key: string;
        const m = moment.tz(s.fromDate, DISPLAY_TIMEZONE);
        if (granularity === 'week') {
            key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
        } else if (granularity === 'month') {
            key = m.clone().startOf('month').format('YYYY-MM-DD');
        } else {
            const fromDate = s.fromDate ? moment.tz(s.fromDate, DISPLAY_TIMEZONE).valueOf() : Date.now();
            key = productionDayKey(fromDate);
        }

        if (!wpMap.has(wpId)) {
            wpMap.set(wpId, { name: wpName, dayMap: new Map() });
        }
        const entry = wpMap.get(wpId)!;
        entry.dayMap.set(key, (entry.dayMap.get(key) || 0) + ok);
    }

    return Array.from(wpMap.entries()).map(([workplaceId, data]) => ({
        workplaceId,
        workplaceName: data.name,
        points: Array.from(data.dayMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([day, okCount]) => ({ day, okCount, movingAvg: null })),
    }));
}

/**
 * INTERNÍ FUNKCE - Výpočet produkce podle projektů z již načtených dat
 */
function computeProductionByProject(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): ProductionByProject[] {

    // projectId -> day -> okCount
    const projectMap = new Map<string, { name: string; dayMap: Map<string, number> }>();

    for (const s of shifts) {
        const productions = (s as any)?.shiftProductions || [];
        
        // Pokud směna nemá productions, použijeme celkový okCount směny
        if (productions.length === 0) {
            const ok = Number(s.okCount ?? 0) || 0;
            if (ok > 0) {
                const projectId = 'noProject';
                const projectName = String((window as any)?.Vue?.$t?.('tpmAnalytics.noProject') || 'Bez projektu');

                let key: string;
                const m = moment.tz(s.fromDate, DISPLAY_TIMEZONE);
                if (granularity === 'week') {
                    key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
                } else if (granularity === 'month') {
                    key = m.clone().startOf('month').format('YYYY-MM-DD');
                } else {
                    const fromDate = s.fromDate ? moment.tz(s.fromDate, DISPLAY_TIMEZONE).valueOf() : Date.now();
                    key = productionDayKey(fromDate);
                }

                if (!projectMap.has(projectId)) {
                    projectMap.set(projectId, { name: projectName, dayMap: new Map() });
                }
                const entry = projectMap.get(projectId)!;
                entry.dayMap.set(key, (entry.dayMap.get(key) || 0) + ok);
            }
        } else {
            for (const p of productions) {
                const ok = Number(p?.okCount ?? 0) || 0;
                if (ok <= 0) continue;
                const projectId = p?.projectId || 'unknown';
                const projectName = (p as any)?.project?.name || projectId || 'N/A';

                let key: string;
                const m = moment.tz(s.fromDate, DISPLAY_TIMEZONE);
                if (granularity === 'week') {
                    key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
                } else if (granularity === 'month') {
                    key = m.clone().startOf('month').format('YYYY-MM-DD');
                } else {
                    const fromDate = s.fromDate ? moment.tz(s.fromDate, DISPLAY_TIMEZONE).valueOf() : Date.now();
                    key = productionDayKey(fromDate);
                }

                if (!projectMap.has(projectId)) {
                    projectMap.set(projectId, { name: projectName, dayMap: new Map() });
                }
                const entry = projectMap.get(projectId)!;
                entry.dayMap.set(key, (entry.dayMap.get(key) || 0) + ok);
            }
        }
    }

    return Array.from(projectMap.entries()).map(([projectId, data]) => ({
        projectId,
        projectName: data.name,
        points: Array.from(data.dayMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([day, okCount]) => ({ day, okCount, movingAvg: null })),
    }));
}

/**
 * INTERNÍ FUNKCE - Výpočet produkce podle směnových týmů z již načtených dat
 */
function computeProductionByShiftTeam(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): ProductionByShiftTeam[] {

    const teamMap = new Map<string, { name: string; dayMap: Map<string, number> }>();

    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        if (ok <= 0) continue;
        
        const teamId = s.shiftTeamTypeId || 'noTeam';
        const teamName = s.shiftTeamTypeId 
            ? ((s as any)?.shiftTeamType?.name || s.shiftTeamTypeId)
            : String((window as any)?.Vue?.$t?.('tpmAnalytics.noShiftTeam') || 'Bez směnového týmu');

        let key: string;
        const m = moment.tz(s.fromDate, DISPLAY_TIMEZONE);
        if (granularity === 'week') {
            key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
        } else if (granularity === 'month') {
            key = m.clone().startOf('month').format('YYYY-MM-DD');
        } else {
            const fromDate = s.fromDate ? moment.tz(s.fromDate, DISPLAY_TIMEZONE).valueOf() : Date.now();
            key = productionDayKey(fromDate);
        }

        if (!teamMap.has(teamId)) {
            teamMap.set(teamId, { name: teamName, dayMap: new Map() });
        }
        const entry = teamMap.get(teamId)!;
        entry.dayMap.set(key, (entry.dayMap.get(key) || 0) + ok);
    }

    return Array.from(teamMap.entries()).map(([shiftTeamTypeId, data]) => ({
        shiftTeamTypeId,
        shiftTeamTypeName: data.name,
        points: Array.from(data.dayMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([day, okCount]) => ({ day, okCount, movingAvg: null })),
    }));
}

/**
 * INTERNÍ FUNKCE - Výpočet produkce podle typů směn z již načtených dat
 */
function computeProductionByShiftType(
    shifts: any[],
    granularity: 'day' | 'week' | 'month'
): ProductionByShiftType[] {

    const typeMap = new Map<string, { name: string; dayMap: Map<string, number> }>();

    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        if (ok <= 0) continue;
        
        const typeId = s.shiftTypeId || 'noType';
        const typeName = s.shiftTypeId 
            ? ((s as any)?.shiftType?.name || s.shiftTypeId)
            : String((window as any)?.Vue?.$t?.('tpmAnalytics.noShiftType') || 'Bez typu směny');

        let key: string;
        const m = moment.tz(s.fromDate, DISPLAY_TIMEZONE);
        if (granularity === 'week') {
            key = m.clone().startOf('isoWeek').format('YYYY-MM-DD');
        } else if (granularity === 'month') {
            key = m.clone().startOf('month').format('YYYY-MM-DD');
        } else {
            const fromDate = s.fromDate ? moment.tz(s.fromDate, DISPLAY_TIMEZONE).valueOf() : Date.now();
            key = productionDayKey(fromDate);
        }

        if (!typeMap.has(typeId)) {
            typeMap.set(typeId, { name: typeName, dayMap: new Map() });
        }
        const entry = typeMap.get(typeId)!;
        entry.dayMap.set(key, (entry.dayMap.get(key) || 0) + ok);
    }

    return Array.from(typeMap.entries()).map(([shiftTypeId, data]) => ({
        shiftTypeId,
        shiftTypeName: data.name,
        points: Array.from(data.dayMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([day, okCount]) => ({ day, okCount, movingAvg: null })),
    }));
}

/**
 * INTERNÍ FUNKCE - Výpočet heatmapy produkce z již načtených dat
 */
function computeProductionHeatmap(
    shifts: any[],
    from: moment.Moment,
    to: moment.Moment
): ProductionHeatmapResult {

    const grid = createGrid(7, 24, 0);
    let totalProduction = 0;

    for (const s of shifts) {
        const ok = Number(s.okCount ?? 0) || 0;
        if (ok <= 0) continue;
        const from = moment(s.fromDate);
        const to = moment(s.toDate);
        
        // Rozložíme produkci proporcionálně po hodinách směny
        const totalMinutes = Math.max(1, to.diff(from, 'minutes'));
        
        // Iterujeme od začátku směny po konec, hodinu po hodině
        let current = from.clone();
        while (current.isBefore(to)) {
            const nextHour = current.clone().add(1, 'hour').startOf('hour');
            const endOfThisHour = moment.min(nextHour, to);
            const minutesInThisHour = endOfThisHour.diff(current, 'minutes');
            
            if (minutesInThisHour > 0) {
                const portion = minutesInThisHour / totalMinutes;
                const dayOfWeek = (current.isoWeekday() - 1 + 7) % 7; // 0=Po, 6=Ne
                const hour = current.hour();
                grid[dayOfWeek][hour] += ok * portion;
            }
            
            current = nextHour;
        }
        
        totalProduction += ok;
    }

    const numDays = Math.max(1, to.diff(from, 'days'));
    const avgPerDay = totalProduction / numDays;

    // Top 3 dny
    const daySums = grid.map((row) => row.reduce((sum, v) => sum + v, 0));
    const topDayIndexes = topNIndexes(daySums, 3);

    // Top 3 hodiny
    const hourSums = Array.from({ length: 24 }, (_, h) => grid.reduce((sum, row) => sum + row[h], 0));
    const topHours = topNIndexes(hourSums, 3);

    return {
        grid,
        totalProduction,
        avgPerDay,
        topDayIndexes,
        topHours,
    };
}

// ============================================================================
// ZPĚTNÁ KOMPATIBILITA - Exportované funkce pro starý kód
// ============================================================================
// Tyto funkce zachovávají původní API, ale interně volají optimalizovanou
// getAllProductionAnalytics() aby se minimalizovaly GraphQL volání

/**
 * Shrnutí produkce OK kusů (celkový počet, průměr, nejproduktivnější den a další statistiky).
 * 
 * @deprecated Použijte raději getAllProductionAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getProductionSummary(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<ProductionSummary> {
    const from = moment(fromIso);
    const to = moment(toIso);
    const span = Math.max(1, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-span, 'minutes');

    const [shiftsNow, shiftsPrev] = await Promise.all([
        ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters),
        ShiftsRepository.fetchShiftsForRange([], prevFrom.toISOString(), prevTo.toISOString(), filters),
    ]);

    return computeProductionSummary(shiftsNow, shiftsPrev, from, to);
}

/**
 * Trend vyrobených OK kusů podle dní/týdnů/měsíců.
 * 
 * @deprecated Použijte raději getAllProductionAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getProductionTrend(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<ProductionTrendPoint[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    return computeProductionTrend(shifts, granularity);
}

/**
 * Produkce OK kusů podle pracovišť (celkový součet za období).
 * 
 * @deprecated Použijte raději getAllProductionAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getProductionByWorkplace(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<ProductionByWorkplace[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    const from = moment(fromIso);
    const to = moment(toIso);
    return computeProductionByWorkplace(shifts, from, to);
}

/**
 * Trend produkce OK kusů podle projektů (časová řada pro každý projekt).
 * 
 * @deprecated Použijte raději getAllProductionAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getProductionByProject(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<ProductionByProject[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    return computeProductionByProject(shifts, granularity);
}

/**
 * Trend produkce OK kusů podle směnových týmů.
 * 
 * @deprecated Použijte raději getAllProductionAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getProductionByShiftTeam(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<ProductionByShiftTeam[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    return computeProductionByShiftTeam(shifts, granularity);
}

/**
 * Trend produkce OK kusů podle typů směn.
 * 
 * @deprecated Použijte raději getAllProductionAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getProductionByShiftType(
    fromIso: string,
    toIso: string,
    granularity: 'day' | 'week' | 'month',
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<ProductionByShiftType[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    return computeProductionByShiftType(shifts, granularity);
}

/**
 * Heatmapa produkce OK kusů podle dne v týdnu (0=Po, 6=Ne) a hodiny (0-23).
 * 
 * @deprecated Použijte raději getAllProductionAnalytics() pro efektivnější načtení všech dat najednou
 */
export async function getProductionHeatmap(
    fromIso: string,
    toIso: string,
    filters?: {
        locationIds?: string[];
        workplaceIds?: string[];
        projectIds?: string[];
        shiftTypeIds?: string[];
        shiftTeamTypeIds?: string[];
    }
): Promise<ProductionHeatmapResult> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    const from = moment(fromIso);
    const to = moment(toIso);
    return computeProductionHeatmap(shifts, from, to);
}


